import { Inject, Injectable } from '@angular/core';
import { LOCAL_STORAGE, StorageService } from 'ngx-webstorage-service';
import { BehaviorSubject, forkJoin, interval, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { Constants } from 'src/app/app.constants';
import { MaintenanceComponent } from '../components/maintenance/maintenance.component';
import { IFilterValues } from './../interfaces/filter.interface';
import { IMasterData, ISSFilterData } from './../interfaces/masterdata.interface';
import { DialogService } from './dialog.service';
import { MasterdataService } from './masterdata.service';
import { Store } from '@ngrx/store';
import { CommonActions, DatasetActions, FaqActions, HomeActions } from 'src/app/store';
import { IMarisData } from '../interfaces/maris-data.interface';

const KEY_CMS = 'maris-cms';
const KEY_MARIS = 'maris-data';

@Injectable({
    providedIn: 'root',
})
export class CacheService {
    private subject$ = new BehaviorSubject<boolean>(false);

    enableCMSStorage: boolean = true;
    enableDataStorage: boolean = true;

    isClosed: boolean = true;
    isDisabled: boolean = false;
    isIndeterminate: boolean = false;
    isSelected: boolean = true;
    isHidden: boolean = false;

    public cmsStorage = {
        news: {},
        stories: {},
        dbupdates: {},
        faqs: {},
    };

    public dataStorage = {
        appVersion: this.constants.appVersion,
        lastUpdate: {
            lastUpdate: '',
            updateFlag: '',
        },
        statistics: [],
        datasets: [],
        filterData: {
            configuration: {
                all: [
                    {
                        id: 'nuclides',
                        value: 'Nuclide',
                        tp: 'dataSelectorTemplate',
                    },
                    {
                        id: 'samplingYears',
                        value: 'Sampling year',
                        tp: 'dataSelectorTemplate',
                    },
                    {
                        id: 'bioGroups',
                        value: 'Taxon',
                        tp: 'dataSelectorTemplate',
                    },
                    {
                        id: 'bodyParts',
                        value: 'Body part',
                        tp: 'dataSelectorTemplate',
                    },
                    {
                        id: 'sedimentTypes',
                        value: 'Sediment type',
                        tp: 'dataSelectorTemplate',
                    },
                    {
                        id: 'dataSets',
                        value: 'Dataset',
                        tp: 'dataSelectorTemplate',
                    },
                    {
                        id: 'samplingDepth',
                        value: 'Sampling depth (m)',
                        tp: 'sliderTemplate',
                    },
                ],
                st1: ['nuclides', 'samplingYears', 'dataSets', 'samplingDepth'],
                st2: ['nuclides', 'samplingYears', 'bioGroups', 'bodyParts', 'dataSets'],
                st3: ['nuclides', 'samplingYears', 'dataSets'], //Hide 'sedimentTypes'
                st4: ['nuclides', 'samplingYears', 'dataSets'],
            },
            filterValues: new FilterValues(),
            defaultFilterValues: {
                ss11: {},
                ss21: {},
                ss31: {},
                ss41: {},
                ss12: {},
                ss22: {},
                ss32: {},
                ss42: {},
            },
        },
        masterData: new MasterData(),
    };

    constructor(
        @Inject(LOCAL_STORAGE) private storage: StorageService,
        private masterdataService: MasterdataService,
        private constants: Constants,
        private dialogService: DialogService,
        private store: Store
    ) {}

    public initCache(force?: boolean) {
        if (!this.storage.has(KEY_CMS)) {
            this.setCMSData();
        }

        if (!this.storage.has(KEY_MARIS)) {
            this.setMarisData();
        }

        if (!this.getMarisData().appVersion || this.getMarisData().appVersion != this.constants.appVersion) {
            this.setCMSData();
            this.setMarisData();
        }

        this.updateMasterData();
        this.initStore();
    }

    private initStore() {
        this.store.dispatch(
            HomeActions.loadStatisticsData({
                statisticsData: this.getMarisData().statistics || [],
            })
        );
        this.store.dispatch(
            FaqActions.loadFaqStoresSuccess({
                data: this.getCMSData().faqs || [],
            })
        );

        this.store.dispatch(
            DatasetActions.loadDatasetsStores({
                data: this.getMarisData().datasets || [],
            })
        );

        this.store.dispatch(
            CommonActions.loadNuclidesSuccess({
                nuclides: this.getMarisData().masterData.nuclide || [],
            })
        );
    }

    public getCMSData() {
        return this.storage.get(KEY_CMS);
    }

    public setCMSData(value?: any) {
        this.storage.set(KEY_CMS, value || this.cmsStorage);
    }

    public getMarisData() {
        return this.storage.get(KEY_MARIS) as IMarisData;
    }

    public setMarisData(value?: any) {
        this.storage.set(KEY_MARIS, value || this.dataStorage);

        if (value?.statistics) {
            this.store.dispatch(
                HomeActions.loadStatisticsData({
                    statisticsData: value.statistics || [],
                })
            );
        }

        if (value?.statistics) {
            this.store.dispatch(
                CommonActions.loadNuclidesSuccess({
                    nuclides: value.masterData.nuclide || [],
                })
            );
        }

        if (value?.datasets) {
            this.store.dispatch(
                DatasetActions.loadDatasetsStores({
                    data: value.datasets || [],
                })
            );
        }
    }

    private checkDBUpdate() {
        this.masterdataService.getDBUpdate().subscribe((data) => {
            this.handleDBUpdate(data);
        });

        interval(120000)
            .pipe(mergeMap(() => this.masterdataService.getDBUpdate()))
            .subscribe((data) => {
                this.handleDBUpdate(data);
            });
    }

    private handleDBUpdate(data: any) {
        if (data.updateFlag == '0') {
            this.dialogService.displayComponent(MaintenanceComponent);
            this.dialogService.setMaintenanceMode(true);
        } else {
            if (this.getMarisData().lastUpdate && this.getMarisData().lastUpdate.lastUpdate == data.lastUpdate) {
                //this.dialogService.displayComponent(null);
                this.dialogService.setMaintenanceMode(false);
            } else {
                let masterData$ = this.masterdataService.getMasterData();

                masterData$.subscribe((result) => {
                    let tempStorage = this.getMarisData();
                    tempStorage.lastUpdate = data;

                    let masterData = result;

                    let masterDataLastUpdate: string = masterData.lastUpdate;
                    let masterDataObj = JSON.parse(masterData.masterData) as IMasterData;

                    masterDataObj.lastUpdate = masterDataLastUpdate;
                    tempStorage.masterData = masterDataObj;

                    this.setMarisData(tempStorage);

                    this.updateStatistics(masterDataObj);
                    this.updateDataSets(masterDataObj);
                    this.updateFilterValues(masterDataObj);
                    this.updateDefaultValues(masterDataObj);
                    this.setCached(true);
                });
            }
        }
    }

    updateMasterData() {
        if (this.getMarisData().masterData && this.getMarisData().masterData.lastUpdate != '') {
            this.setCached(true);
            this.checkDBUpdate();
        } else {
            let lastUpdate$ = this.masterdataService.getDBUpdate();
            let masterData$ = this.masterdataService.getMasterData();

            forkJoin([lastUpdate$, masterData$]).subscribe((result) => {
                let lastUpdate = result[0];
                let masterData = result[1];

                let tempStorage = this.getMarisData();

                let masterDataLastUpdate: string = masterData.lastUpdate;
                let masterDataObj = JSON.parse(masterData.masterData) as IMasterData;

                tempStorage.lastUpdate = lastUpdate;

                masterDataObj.lastUpdate = masterDataLastUpdate;
                tempStorage.masterData = masterDataObj;

                this.setMarisData(tempStorage);

                this.updateStatistics(masterDataObj);
                this.updateDataSets(masterDataObj);
                this.updateFilterValues(masterDataObj);
                this.updateDefaultValues(masterDataObj);
                this.setCached(true);

                this.checkDBUpdate();
            });
        }

        // this.masterdataService.getMasterData().pipe(
        //   take(1),
        // ).subscribe((result: IMasterDataResult) => {
        //   let tempStorage = this.getMarisData();

        //   let lastUpdate: string = result.lastUpdate;
        //   let masterDataObj = JSON.parse(result.masterData) as IMasterData;

        //   masterDataObj.lastUpdate = lastUpdate;
        //   tempStorage.masterData = masterDataObj;

        //   this.setMarisData(tempStorage);

        //   this.updateStatistics(masterDataObj);
        //   this.updateDataSets(masterDataObj);
        //   this.updateFilterValues(masterDataObj);
        //   this.updateDefaultValues(masterDataObj);
        //   this.setCached(true);
        // })
    }

    updateStatistics(masterDataObj) {
        let tempStorage = this.getMarisData();
        tempStorage.statistics = masterDataObj.statistics;
        this.setMarisData(tempStorage);
    }

    updateDataSets(masterDataObj) {
        let tempStorage = this.getMarisData();
        let ref = masterDataObj.reference.map((item) => {
            return {
                displayText: item.lb,
                fullText: item.desc,
                publicationYear: item.year,
                refId: item.id,
                zoteroUrl: item.url,
                sampleTypes: item.stype,
            };
        });
        // .sort(this.dynamicSort('publicationYear'))
        // .sort(this.dynamicSort('displayText'));

        tempStorage.datasets = ref;
        this.setMarisData(tempStorage);
    }

    updateFilterValues(masterDataObj) {
        let sampleType = masterDataObj.sampletype.sort(this.dynamicSort('id'));
        let shapeType = masterDataObj.shapetype.sort(this.dynamicSort('id'));
        let seaAreas = this.mapFilterValue(masterDataObj.area, true, 'seaAreas');
        let nuclides = masterDataObj.nuclide;
        let samplingYears = masterDataObj.samplingyear.sort(this.dynamicSort('id'));
        let dataSets = masterDataObj.reference
            .map((data) => {
                return { id: data.id, lb: data.lb, year: data.year };
            })
            .sort(this.dynamicSort('year'))
            .sort(this.dynamicSort('lb'));
        let samplingDepth = masterDataObj.sampdepth[0];
        let bioGroups = this.mapFilterValue(masterDataObj.biogroup, true, 'bioGroups');
        let bodyParts = masterDataObj.bodypar.sort(this.dynamicSort('lb'));
        let sedimentTypes = masterDataObj.sedtype.sort(this.dynamicSort('lb'));

        let tempStorage = this.getMarisData();

        tempStorage.filterData.filterValues.sampleType = this.mapFilterValue(sampleType, false);
        tempStorage.filterData.filterValues.shapeType = this.mapFilterValue(shapeType, false);
        tempStorage.filterData.filterValues.seaAreas = seaAreas.reduce((r, a) => {
            r['sh' + a.sid] = r['sh' + a.sid] || [];
            r['sh' + a.sid].push(a);
            return r;
        }, Object.create(null));
        tempStorage.filterData.filterValues.nuclides = this.mapFilterValue(nuclides, false, 'nuclides');
        tempStorage.filterData.filterValues.samplingYears = this.mapFilterValue(samplingYears, false, 'samplingYear');
        tempStorage.filterData.filterValues.dataSets = this.mapFilterValue(dataSets, false, 'dataSets');
        tempStorage.filterData.filterValues.samplingDepth = samplingDepth;
        tempStorage.filterData.filterValues.bioGroups = bioGroups;
        tempStorage.filterData.filterValues.bodyParts = this.mapFilterValue(bodyParts, false, 'bodyPart');
        tempStorage.filterData.filterValues.sedimentTypes = this.mapFilterValue(sedimentTypes, false, 'sedimentType');

        this.setMarisData(tempStorage);
    }

    updateDefaultValues(masterDataObj) {
        let tempStorage = this.getMarisData();

        let _ss11 = JSON.parse(masterDataObj.ss11[0].filtervalue);
        let _ss21 = JSON.parse(masterDataObj.ss21[0].filtervalue);
        let _ss31 = JSON.parse(masterDataObj.ss31[0].filtervalue);
        let _ss41 = JSON.parse(masterDataObj.ss41[0].filtervalue);
        let _ss12 = JSON.parse(masterDataObj.ss12[0].filtervalue);
        let _ss22 = JSON.parse(masterDataObj.ss22[0].filtervalue);
        let _ss32 = JSON.parse(masterDataObj.ss32[0].filtervalue);
        let _ss42 = JSON.parse(masterDataObj.ss42[0].filtervalue);

        let ss11, ss12;
        ss11 = {
            sampleType: 1,
            seaAreas: { count: 0, data: [] },
            nuclides: { count: 0, data: [] },
            samplingYears: { count: 0, data: [] },
            dataSets: { count: 0, data: [] },
            samplingDepth: { min: 0, max: 0 },
        };

        ss12 = {
            sampleType: 1,
            seaAreas: { count: 0, data: [] },
            nuclides: { count: 0, data: [] },
            samplingYears: { count: 0, data: [] },
            dataSets: { count: 0, data: [] },
            samplingDepth: { min: 0, max: 0 },
        };

        let ss21, ss22;
        ss21 = {
            sampleType: 2,
            seaAreas: { count: 0, data: [] },
            nuclides: { count: 0, data: [] },
            samplingYears: { count: 0, data: [] },
            dataSets: { count: 0, data: [] },
            bioGroups: { count: 0, data: [] },
            bodyParts: { count: 0, data: [] },
        };

        ss22 = {
            sampleType: 2,
            seaAreas: { count: 0, data: [] },
            nuclides: { count: 0, data: [] },
            samplingYears: { count: 0, data: [] },
            dataSets: { count: 0, data: [] },
            bioGroups: { count: 0, data: [] },
            bodyParts: { count: 0, data: [] },
        };

        let ss31, ss32;
        ss31 = {
            sampleType: 3,
            seaAreas: { count: 0, data: [] },
            nuclides: { count: 0, data: [] },
            samplingYears: { count: 0, data: [] },
            dataSets: { count: 0, data: [] },
            sedimentTypes: { count: 0, data: [] },
        };

        ss32 = {
            sampleType: 3,
            seaAreas: { count: 0, data: [] },
            nuclides: { count: 0, data: [] },
            samplingYears: { count: 0, data: [] },
            dataSets: { count: 0, data: [] },
            sedimentTypes: { count: 0, data: [] },
        };

        let ss41, ss42;
        ss41 = {
            sampleType: 4,
            seaAreas: { count: 0, data: [] },
            nuclides: { count: 0, data: [] },
            samplingYears: { count: 0, data: [] },
            dataSets: { count: 0, data: [] },
        };

        ss42 = {
            sampleType: 4,
            seaAreas: { count: 0, data: [] },
            nuclides: { count: 0, data: [] },
            samplingYears: { count: 0, data: [] },
            dataSets: { count: 0, data: [] },
        };

        let source = tempStorage.filterData.filterValues;
        let data = tempStorage.filterData.defaultFilterValues;

        let target = [ss11, ss21, ss31, ss41, ss12, ss22, ss32, ss42];
        let input = [_ss11, _ss21, _ss31, _ss41, _ss12, _ss22, _ss32, _ss42];

        this.mapDefaultFilterValue(source.seaAreas.sh1, [ss11, ss21, ss31, ss41], [_ss11, _ss21, _ss31, _ss41], 'seaAreas');
        this.mapDefaultFilterValue(source.seaAreas.sh2, [ss12, ss22, ss32, ss42], [_ss12, _ss22, _ss32, _ss42], 'seaAreas');
        this.mapDefaultFilterValue(source.nuclides, target, input, 'nuclides');
        this.mapDefaultFilterValue(source.samplingYears, target, input, 'samplingYears');
        this.mapDefaultFilterValue(source.dataSets, target, input, 'dataSets');
        this.mapDefaultFilterValue(source.samplingDepth, [ss11, ss12], [_ss11, _ss12], 'samplingDepth');
        this.mapDefaultFilterValue(source.bioGroups, [ss21, ss22], [_ss21, _ss22], 'bioGroups');
        this.mapDefaultFilterValue(source.bodyParts, [ss21, ss22], [_ss21, _ss22], 'bodyParts');
        this.mapDefaultFilterValue(source.sedimentTypes, [ss31, ss32], [_ss31, _ss32], 'sedimentTypes');

        data.ss11 = ss11;
        data.ss21 = ss21;
        data.ss31 = ss31;
        data.ss41 = ss41;
        data.ss12 = ss12;
        data.ss22 = ss22;
        data.ss32 = ss32;
        data.ss42 = ss42;

        this.setMarisData(tempStorage);
    }

    isCached(): Observable<boolean> {
        return this.subject$;
    }

    setCached(cached: boolean) {
        this.subject$.next(cached);
    }

    dynamicSort(property) {
        var sortOrder = 1;
        if (property[0] === '-') {
            sortOrder = -1;
            property = property.substr(1);
        }
        return function (a, b) {
            if (typeof a[property] == 'number') {
                return a[property] - b[property];
            } else {
                return a[property]?.toLowerCase() < b[property]?.toLowerCase() ? -1 : a[property]?.toLowerCase() > b[property]?.toLowerCase() ? 1 : 0;
            }
        };
    }

    dynamicSortMultiple(props: string[]) {
        return function (obj1, obj2) {
            return props
                .map((o) => {
                    var dir = 1;
                    if (o[0] === '-') {
                        dir = -1;
                        o = o.substring(1);
                    }
                    if (typeof obj1[o] == 'number') {
                        if (obj1[o] > obj2[o]) return dir;
                        if (obj1[o] < obj2[o]) return -dir;
                    } else {
                        if (obj1[o].toLowerCase() > obj2[o].toLowerCase()) return dir;
                        if (obj1[o].toLowerCase() < obj2[o].toLowerCase()) return -dir;
                    }
                    return 0;
                })
                .reduce((p, n) => (p ? p : n), 0);
        };
    }

    mapFilterValue(filterValue: any, hasParentChild: boolean, key?: string) {
        let result = [];
        if (hasParentChild) {
            if (key == 'seaAreas') {
                result = filterValue
                    .map((data) => {
                        return {
                            id: data.id,
                            value: data.lb.replace('&amp;', '&'),
                            sid: data.sid ? data.sid : null,
                            isDisabled: this.isDisabled,
                            isSelected: this.isSelected,
                            isIndeterminate: this.isIndeterminate,
                            isHidden: this.isHidden,
                            isClosed: this.isClosed,
                            childList: data.subarea
                                ? JSON.parse('[' + data.subarea.replace('&amp;#x0D;', '').replace(/\n/g, '') + ']')
                                      .map((child) => {
                                          return {
                                              id: child.id,
                                              parent_id: child.pid,
                                              value: child.lb,
                                              isDisabled: this.isDisabled,
                                              isSelected: this.isSelected,
                                              isHidden: this.isHidden,
                                          };
                                      })
                                      .sort(this.dynamicSort('value'))
                                : [
                                      {
                                          id: data.id,
                                          parent_id: data.pid,
                                          value: data.lb,
                                          isDisabled: this.isDisabled,
                                          isSelected: this.isSelected,
                                          isHidden: this.isHidden,
                                      },
                                  ],
                        };
                    })
                    .sort(this.dynamicSort('value'));
            }

            if (key == 'bioGroups') {
                result = filterValue
                    .map((data) => {
                        return {
                            id: data.id,
                            value: data.lb.replace('&amp;', '&'),
                            isDisabled: this.isDisabled,
                            isSelected: this.isSelected,
                            isIndeterminate: this.isIndeterminate,
                            isHidden: this.isHidden,
                            isClosed: this.isClosed,
                            childList: data.species
                                ? JSON.parse('[' + data.species + ']')
                                      .map((child) => {
                                          return {
                                              id: child.id,
                                              value: child.lb,
                                              isDisabled: this.isDisabled,
                                              isSelected: this.isSelected,
                                              isHidden: this.isHidden,
                                          };
                                      })
                                      .sort(this.dynamicSort('value'))
                                : [
                                      {
                                          id: -1,
                                          value: '',
                                          isDisabled: this.isDisabled,
                                          isSelected: this.isSelected,
                                          isHidden: this.isHidden,
                                      },
                                  ],
                        };
                    })
                    .sort(this.dynamicSort('value'));
            }
        } else {
            if (key == 'nuclides') {
                result = filterValue.map((data) => {
                    return {
                        id: data.id,
                        value: data.lb,
                        massnb: data.massnb,
                        sampleType: data.samptype,
                        isDisabled: this.isDisabled,
                        isSelected: this.isSelected,
                        isHidden: this.isHidden,
                    };
                });
            } else {
                result = filterValue.map((data) => {
                    return {
                        id: data.id,
                        value: data.lb,
                        isDisabled: this.isDisabled,
                        isSelected: this.isSelected,
                        isHidden: this.isHidden,
                    };
                });
            }
        }

        return result;
    }

    mapDefaultFilterValue(source, target, input, property) {
        switch (property) {
            case 'seaAreas':
                target.forEach((el, idx) => {
                    let subarea_count = 0;
                    source.forEach((item) => {
                        let child = item.childList.filter((child) => {
                            return input[idx].seaAreas.includes(child.id.trim());
                        });
                        subarea_count += child.length;
                        if (child.length > 0) {
                            el.seaAreas.data.push({
                                id: item.id,
                                isClosed: this.isClosed,
                                isDisabled: this.isDisabled,
                                isSelected: this.isSelected,
                                isIndeterminate: this.isIndeterminate,
                                isHidden: this.isHidden,
                                sid: item.sid,
                                value: item.value,
                                childList: child,
                            });
                        }
                    });
                    el.seaAreas.count = subarea_count;
                });

                break;

            case 'bioGroups':
                target.forEach((el, idx) => {
                    let species_count = 0;
                    source.forEach((item) => {
                        let child = item.childList.filter((child) => {
                            return input[idx].bioGroups.includes(child.id);
                        });
                        species_count += child.length;
                        if (child.length > 0) {
                            el.bioGroups.data.push({
                                id: item.id,
                                isClosed: this.isClosed,
                                isDisabled: this.isDisabled,
                                isSelected: this.isSelected,
                                isIndeterminate: this.isIndeterminate,
                                isHidden: this.isHidden,
                                value: item.value,
                                childList: child,
                            });
                        }
                    });
                    el.bioGroups.count = species_count;
                });

                break;

            case 'samplingDepth':
                target.forEach((el) => {
                    el.samplingDepth = source;
                });

                break;

            default:
                target.forEach((el, idx) => {
                    var temp = source;
                    if (property == 'nuclides') {
                        temp = source.filter((data) => {
                            return data.sampleType == el.sampleType;
                        });
                    }
                    temp.forEach((item) => {
                        if (input[idx][property].includes(item.id)) {
                            el[property].data.push(item);
                            el[property].count += 1;
                        }
                    });
                });
                break;
        }
    }
}

class MasterData implements IMasterData {
    ss11: ISSFilterData[];
    ss12: ISSFilterData[];
    ss21: ISSFilterData[];
    ss22: ISSFilterData[];
    ss31: ISSFilterData[];
    ss32: ISSFilterData[];
    ss41: ISSFilterData[];
    ss42: ISSFilterData[];
    lastUpdate: string = '';
    statistics: { id: number; type: string; total: number; icon: string }[] = [];
    sampleType: { id: number; lb: string }[] = [];
    shapeType: { id: number; lb: string }[] = [];
    seaArea: { id: string; sid: number; lb: string; subarea: string }[] = [];
    nuclide: { id: number; lb: string; massnb: number; samptype: number }[] = [];
    biogroup: { id: number; lb: string; species: string }[] = [];
    bodypar: { id: number; lb: string }[] = [];
    sedType: { id: number; lb: string }[] = [];
    reference: {
        id: number;
        lb: string;
        desc: string;
        url: string;
        stype: string;
        year: number;
    }[] = [];
    sampleDepth: { min: number; max: number }[] = [];
    samplingYear: { id: number; lb: number }[] = [];
}

class FilterValues implements IFilterValues {
    sampleType: { id: number; lb: string }[] = [];
    shapeType: { id: number; lb: string }[] = [];
    seaAreas: {
        id: string;
        sid: number;
        lb: string;
        subArea: { id: number; lb: string }[];
    }[] = [];
    nuclides: { id: number; lb: string }[] = [];
    samplingYears: { id: number; lb: string }[] = [];
    dataSets: { id: number; lb: string }[] = [];
    samplingDepth: { min: number; max: number } = { min: 0, max: 0 };
    bioGroups: {
        id: number;
        lb: string;
        species: { id: number; lb: string }[];
    }[] = [];
    bodyParts: { id: number; lb: string }[] = [];
    sedimentTypes: { id: number; lb: string }[] = [];
}
